import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface ContactConnection {
  id: string;
  contact_id: string;
  sender_id: string;
  sender_name: string | null;
  connected_at: string | null;
  source: string | null;
  match_confidence: number | null;
}

/**
 * Bulk-load all contact_connections with sender names resolved.
 * Table is small (hundreds of rows at most) — single fetch cached 5 min.
 */
export function useContactConnectionIndex() {
  return useQuery({
    queryKey: ["contact-connections-index"],
    queryFn: async () => {
      const { data: conns, error } = await supabase
        .from("contact_connections" as any)
        .select("id, contact_id, sender_id, connected_at, source, match_confidence");
      if (error) throw error;
      const rows = (conns || []) as unknown as Array<{
        id: string;
        contact_id: string;
        sender_id: string;
        connected_at: string | null;
        source: string | null;
        match_confidence: number | null;
      }>;

      // Resolve sender names in one query
      const senderIds = [...new Set(rows.map((r) => r.sender_id))];
      const senderMap = new Map<string, string>();
      if (senderIds.length > 0) {
        const { data: senders } = await supabase
          .from("outreach_senders" as any)
          .select("id, name")
          .in("id", senderIds);
        if (senders) {
          for (const s of senders as any[]) {
            senderMap.set(s.id, s.name);
          }
        }
      }

      const byContact = new Map<string, ContactConnection>();
      const contactIds = new Set<string>();
      for (const r of rows) {
        contactIds.add(r.contact_id);
        if (!byContact.has(r.contact_id)) {
          byContact.set(r.contact_id, {
            ...r,
            sender_name: senderMap.get(r.sender_id) ?? null,
          });
        }
      }
      return { byContact, contactIds };
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Check if a specific contact has a recorded connection.
 * Returns sender name if connected, null otherwise.
 */
export function useIsConnected(contactId: string | null | undefined) {
  const { data } = useContactConnectionIndex();
  if (!data || !contactId) return { connected: false, entry: null as ContactConnection | null };
  const entry = data.byContact.get(contactId) ?? null;
  return { connected: !!entry, entry };
}
